/**
 * How long a just-cued row stays in Nearby, so the button can be *seen* to say
 * `Sent`.
 *
 * The confirmation was real but invisible. Sending sets `sentCueIds` and the
 * row's button flips to a disabled `Sent`; `refreshSentCues()` then runs
 * immediately after and filters that row out. That is one round trip — tens of
 * milliseconds on a warm connection — so the row disappeared in about the time
 * the press animation took, and the sender was left with a list that had
 * silently lost someone rather than a confirmation they could read.
 *
 * The server agrees the peer is no longer available the instant the Cue exists
 * (`presentRows` filters `pendingCuePeerIds`), so this is deliberately a
 * *presentation* hold on the client and nothing more. It defers a removal the
 * client was going to do anyway; it never invents a row, and a real reload —
 * pull-to-refresh, or returning to the tab — legitimately drops the peer
 * straight away, because by then the sender has moved on.
 *
 * Deliberately free of imports, like `launch-refresh.ts`: `session-store.ts`
 * pulls `react-native`, `@/api` and `@/i18n` and so cannot be loaded in a test,
 * and a hold whose expiry is only ever read is not a hold anyone has checked.
 */

/** A beat. Long enough to register, short enough not to feel like a stall. */
export const SENT_ROW_LINGER_MS = 1000;

/** peer id -> the instant their row may be removed. */
const lingering = new Map<string, number>();

export function holdSentRow(peerId: string, now: number = Date.now()): void {
  lingering.set(peerId, now + SENT_ROW_LINGER_MS);
}

export function releaseSentRow(peerId: string): void {
  lingering.delete(peerId);
}

/**
 * Whether this peer's row is still being held open.
 *
 * Prunes as it reads, so the map is bounded by the Cues sent inside one linger
 * window rather than by the length of the session — nothing else sweeps it.
 */
export function stillLingering(peerId: string, now: number = Date.now()): boolean {
  const until = lingering.get(peerId);
  if (until === undefined) return false;
  if (until > now) return true;
  lingering.delete(peerId);
  return false;
}

/** Sign-out, and tests. A hold must never outlive the session that made it. */
export function clearSentRowHolds(): void {
  lingering.clear();
}

/** Only for tests: how many holds are outstanding. */
export function outstandingHolds(): number {
  return lingering.size;
}
